import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useFetchVersionsQuery } from '../redux/api/versionManagment';
import { IVersion } from '../common/interfaces/Version.interface';
import { PageHeader } from '../components/Shared/PageHeader';
import { Table } from 'flowbite-react';
import { TableCommon, TableCell } from '../components/Table/Table';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { Loading } from '../components/Shared/Loading/Loading';
import AddVersionModal from '../components/VersionModal/AddVersionModal';
import { EditButton } from '../components/Shared/EditButton';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import { canAddVersion } from '../utils/versions';

export const VersionManagmentPage: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedVersion, setSelectedVersion] = React.useState<IVersion | null>(
    null
  );
  usePageTitle(t('pageTitles.versionsManagement'));

  const {
    data: response,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useFetchVersionsQuery({
    // One record per OS: ask for the backend's largest page so no record is ever hidden.
    page: 1,
    size: 50,
  });

  const versionsList: IVersion[] = response?.data || [];
  const allowAddVersion = canAddVersion(versionsList);

  const handleAddVersion = () => {
    setIsModalOpen(true);
  };

  const columnHeadLabels = [
    { id: 'appVersion', label: t('versionsManagement.appVersion') },
    { id: 'osType', label: t('versionsManagement.os') },
    { id: 'link', label: t('versionsManagement.link') },
    {
      id: 'forceUpdateRequired',
      label: t('versionsManagement.forceUpdateRequired'),
    },
    { id: 'edit', label: t('versionsManagement.editVersion') },
  ];

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (isLoading) return <Loading />;
  return (
    <>
      <PageHeader
        title={t('sidebar.versions')}
        {...(allowAddVersion && {
          buttonText: t('sidebar.addVersion'),
          buttonOnClick: handleAddVersion,
        })}
      />
      {versionsList.length > 0 ? (
        <TableCommon columns={columnHeadLabels}>
          {versionsList.map((item) => (
            <Table.Row key={item.id} className="bg-white text-center">
              <TableCell>{item.appVersion}</TableCell>
              <TableCell>{item.osType}</TableCell>
              <TableCell>
                <a
                  href={item.link}
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('versionsManagement.link')}
                </a>
              </TableCell>
              <TableCell>
                {item.forceUpdateRequired
                  ? t('versionsManagement.yes')
                  : t('versionsManagement.no')}
              </TableCell>
              <TableCell>
                <EditButton
                  onClick={() => {
                    setSelectedVersion(item);
                    setIsModalOpen(true);
                  }}
                />
              </TableCell>
            </Table.Row>
          ))}
        </TableCommon>
      ) : (
        <EmptyState />
      )}
      <AddVersionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedVersion(null);
        }}
        versionToEdit={selectedVersion}
      />
    </>
  );
};
